import { styled } from "@mui/material";
import SearchMatch from "_shared/_views/tables/search-match";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import type { MRT_PaginationState as PaginationState } from "material-react-table";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import {
  getFeeScheduleTeamMembers,
  withFeeScheduleStaffingSeated,
} from "_shared/fee-schedules/categories/fee-schedule-team-members";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import useReconcileFeeScheduleStaffing from "_shared/fee-schedules/categories/use-reconcile-fee-schedule-staffing";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/masked-fields";
import { useGetFeeScheduleTemplatesQuery } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
import {
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import { isFeeScheduleTemplateLoadIssue } from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import { TemplateTagLine } from "work/fee-schedule-templates/_views/_shared/template-name-with-tags";
import FeeScheduleTemplatesTable, {
  type FeeScheduleDraftChanges,
} from "work/fee-schedule-templates/_views/tables/fee-schedule-templates-table";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import ProposalIssueList from "work/proposals/issues/_views/proposal-issue-list";
import ProposalIssues, { feeScheduleCategoryIssues, type ProposalIssue, ProposalIssuePart } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { type TemplateOwnershipScope } from "_shared/_views/templates/template-ownership-filter";
import TemplateSelector, { type TemplateSelectorRow } from "_shared/_views/templates/template-selector";
import useTemplateNameOrder from "_shared/_views/templates/use-template-name-order";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import useCreateFeeScheduleTemplateAction, {
  type CreatedFeeScheduleTemplate,
} from "work/proposals/_views/_shared/use-create-fee-schedule-template-action";
import {
  NO_TEMPLATE_CONTRIBUTIONS,
  TemplateContributions,
  withTemplateDeselected,
  withTemplateSelected,
} from "work/proposals/_views/_shared/template-contributions";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
  workPaneNarrowerThan,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";
import {
  applyPublishedFeeScheduleToDraft,
  publishedFeeScheduleTeamId,
} from "work/proposals/_views/drafts/tabs/apply-published-fee-schedule";
import PublishedFeeScheduleAction from "work/proposals/_views/drafts/tabs/published-fee-schedule-action";
import SavedDraftCollections from "work/proposals/_views/drafts/saved-draft-collections";

const ListContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  flexWrap: "nowrap",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
  },
}));
const FeeScheduleContent = styled(Grid)(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
}));
/** A category's name in the row a template's expander reveals. */
const TemplateRowName = styled(Typography)(() => ({
  flex: "0 1 auto",
  fontSize: "1em",
  fontWeight: 500,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const CategoryRowText = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));
const CategoryRowDetail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  lineHeight: 1.3,
  minWidth: 0,
  overflow: "hidden",
}));
const CategorySelection = styled(FeeScheduleTemplatesTable)(({ theme }) => ({
  height: "100%",
  flex: 1,
  minHeight: 0,
  marginLeft: theme.spacing(0),
  minWidth: 0,
  overflow: "hidden",
}));
/** The tab's issues, inset to line up with the table toolbar underneath. */
const TabIssueList = styled(ProposalIssueList)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    paddingInline: theme.spacing(0.5),
  },
  // On a phone the notice lines up with the templates expander above it and the toolbar below (#5907).
  [workPaneNarrowerThan(theme.breakpoints.values.sm)]: {
    paddingInline: 0,
  },
  marginBottom: theme.spacing(1),
  paddingInline: theme.spacing(1),
}));

type SelectFeeScheduleProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  showPendingChangeHighlights?: boolean;
  isDrawerOpen: boolean;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
  onCreateCategoryClicked: () => void;
  onAddExistingCategoryClicked: () => void;
  onEditCategory: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  /** Moves the page to the category an issue is about, as "Prev / Next Issue" would. */
  onTraverseToNewField?: (field: ProposalField) => void;
  /**
   * Answers a press on one of the tab's issues (#6097). The page moves to the issue's category, and
   * opens its editor on the Team Member field when the issue is about who bills the rate. Without it,
   * a press only moves to the category, as "Prev / Next Issue" would.
   */
  onIssueSelected?: (issue: ProposalIssue) => void;
  /**
   * Hears the schedule's categories in the order its table draws them, so Prev / Next walks the rows
   * as the reader sorted them (#6617).
   */
  onDisplayedCategoriesChange?: (categoryIds: readonly Guid[] | undefined) => void;
  /** The draft as last saved; undefined for a draft that has never been saved, which shows no Undo. */
  savedCollections?: SavedDraftCollections;
};
type FeeScheduleTemplateCategory = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;

export default function FeeScheduleTab(props: Readonly<SelectFeeScheduleProps>) {
  const INTERFACE_NAME = "DraftProposalFeeScheduleTab";

  const {
    activeReviewField,
    proposalBuilder,
    showPendingChangeHighlights,
    isDrawerOpen,
    disableEditing,
    fieldLockTooltip,
    negotiationId,
    viewerScope,
    onProposalBuilderUpdated,
    onCreateCategoryClicked,
    onAddExistingCategoryClicked,
    onEditCategory,
    onTraverseToNewField,
    onIssueSelected,
    onDisplayedCategoriesChange,
    savedCollections,
  } = props;

  const isMdDownDisplay = useWorkPaneIsCompact();
  const session = useSession();
  const userId = session.user!.id;
  const userLegalEntityId = session.user!.legalEntityId;

  const [templateOwnershipScope, setTemplateOwnershipScope] = useState<TemplateOwnershipScope>("all");
  const [templateSearch, setTemplateSearch] = useState("");
  const templateOrder = useTemplateNameOrder();
  const [templatePagination, setTemplatePagination] = useState<PaginationState>(INITIAL_TEMPLATE_PAGINATION);
  const [templateContributions, setTemplateContributions] =
    useState<TemplateContributions>(NO_TEMPLATE_CONTRIBUTIONS);
  const selectedCategories = Masked.isPresentNotMasked(proposalBuilder?.currentSpec?.workAgreement?.feeSchedule)
    ? proposalBuilder.currentSpec.workAgreement.feeSchedule
    : [];
  const selectedTemplateIds = session.context?.viewingAsVendor
    ? proposalBuilder.currentSpec.workAgreement.vendorFeeScheduleTemplateIds
    : proposalBuilder.currentSpec.workAgreement.clientFeeScheduleTemplateIds;
  // Who this draft can already bill. A template may name anyone in the firm, so applying one asks
  // what to do about anybody missing from here. See `useReconcileFeeScheduleStaffing`.
  const currentVendorTeam = Masked.isPresentNotMasked(proposalBuilder?.currentSpec?.workAgreement?.vendorTeam)
    ? proposalBuilder.currentSpec.workAgreement.vendorTeam
    : undefined;
  const vendorTeamMembers = getFeeScheduleTeamMembers(currentVendorTeam);
  // An empty roster is not the same as an unreadable one, and only one of them can be added to.
  const vendorTeamIsMasked = Masked.isMasked(proposalBuilder?.currentSpec?.workAgreement?.vendorTeam);
  // Who this draft's rates may name, which leaves out the client's own firm (#6091).
  const teamRoster = FeeScheduleTeamRoster.fromVendorTeam(
    proposalBuilder?.currentSpec?.workAgreement?.vendorTeam,
    proposalBuilder?.currentSpec?.workAgreement?.clientTeam,
  );
  const reconcileStaffing = useReconcileFeeScheduleStaffing();
  // The draft and its update handler as of the latest render. Applying published rates waits on the
  // staffing question, and a collaborator's save can land while it is open; finishing on the props
  // the press started from would queue their changes back out as reversions.
  const latestDraftRef = useRef({ proposalBuilder, onProposalBuilderUpdated, reconcileStaffing });
  useEffect(() => {
    latestDraftRef.current = { proposalBuilder, onProposalBuilderUpdated, reconcileStaffing };
  });
  // Whose published rates this draft is offered: the vendor's own team's, and never on the client's
  // draft (#4920).
  const publishedRatesTeamId = publishedFeeScheduleTeamId({
    viewingAsVendor: session.context?.viewingAsVendor,
    vendorTeam: currentVendorTeam,
  });
  const selectedTemplateCount = selectedTemplateIds?.length ?? 0;
  const pendingChangeFields = showPendingChangeHighlights
    ? proposalBuilder.sessionHistory?.flatMap((change) => change.field ? [change.field] : []) ?? []
    : [];

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [ProposalField.FeeSchedule, ProposalField.FeeScheduleCategory(issue.field.id ?? Guid.EMPTY)].some((field) =>
          field.isEqualTo(issue.field),
        ),
      ),
    [props.issues?.entries],
  );
  const teamMemberIssues = React.useMemo(
    () => feeScheduleCategoryIssues(issues, ProposalIssuePart.TeamMember),
    [issues],
  );
  const feeIssues = React.useMemo(() => feeScheduleCategoryIssues(issues, ProposalIssuePart.Fee), [issues]);

  /** A new search means a new first page: page two of the old search is not page two of this one. */
  function handleTemplateSearchChanged(value: string) {
    setTemplateSearch(value);
    setTemplatePagination((current) => ({ ...current, pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX }));
  }

  const templateQueryParams = useMemo(() => {
    const paginationParameters = PaginationParameters.create(templatePagination);
    const filterParameters = new FeeScheduleTemplateFilterParameters({
      entityId: userLegalEntityId,
      creatorUserId: templateOwnershipScope === "own" ? userId : undefined,
      query: templateSearch.trim() || undefined,
    });
    const orderParameters = new FeeScheduleTemplateOrderParameters({
      orderBy: FeeScheduleTemplateOrderField.Name,
      orderDir: templateOrder.orderDirection,
    });
    return new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
    });
  }, [templateOrder.orderDirection, templateOwnershipScope, templatePagination, templateSearch, userId, userLegalEntityId]);

  const templatesQueryArgs = useMemo(
    () => ({ query: templateQueryParams, interfaceName: INTERFACE_NAME }),
    [templateQueryParams],
  );
  const templatesQuery = useGetFeeScheduleTemplatesQuery(templatesQueryArgs);
  const paginatedTemplatesJSON = templatesQuery.data;
  // RTK Query's last answer, kept while a larger request loads or after it fails, so the panel keeps
  // the rows it had and offers the next ones again (review of #6896).
  const templateFeeSchedules = useMemo(
    () => FeeScheduleTemplate.fromJSONArray(paginatedTemplatesJSON?.data ?? []),
    [paginatedTemplatesJSON?.data],
  );

  async function handleTemplateSelectionToggled(template: FeeScheduleTemplate) {
    let updatedSelectedTemplateIds = session.context?.viewingAsVendor
      ? [...(proposalBuilder.currentSpec.workAgreement.vendorFeeScheduleTemplateIds ?? [])]
      : [...(proposalBuilder.currentSpec.workAgreement.clientFeeScheduleTemplateIds ?? [])];
    try {
      if (!template.id) throw new Error("No template selected");

      let updatedProposalBuilder = proposalBuilder;
      if (updatedSelectedTemplateIds.some((id) => id.isEqualTo(template.id))) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
        const { contributions, itemKeysToRemove: categoryKeysToRemove } = withTemplateDeselected({
          contributions: templateContributions,
          templateId: template.id,
          alsoSuppliedItemKeys: getCategoryKeysOfLoadedTemplates(updatedSelectedTemplateIds),
        });
        if (categoryKeysToRemove.size) {
          updatedProposalBuilder = proposalBuilder.setFeeSchedule(
            selectedCategories.filter((category) => !categoryKeysToRemove.has(getCategoryKey(category))),
          );
        }
        setTemplateContributions(contributions);
      } else {
        updatedSelectedTemplateIds.push(template.id);
        // The template's rates say who they are for. Anyone it names who is not on this draft's
        // vendor team is put to the user before anything is applied (#5237).
        const staffing = await reconcileStaffing({
          categories: template.categories,
          teamMembers: vendorTeamMembers,
          teamIsMasked: vendorTeamIsMasked,
          excludedLegalEntityId: teamRoster.excludedLegalEntityId,
        });
        if (!staffing.applied) return;
        if (staffing.membersToAddToTeam.length > 0) {
          updatedProposalBuilder = updatedProposalBuilder.setVendorTeam(
            withFeeScheduleStaffingSeated(currentVendorTeam ?? new VendorTeam({ members: [] }), staffing.membersToAddToTeam),
          );
        }
        const categories = _.uniqWith(
          [...selectedCategories, ...staffing.categories],
          (a, b) => a.name?.isEqualTo(b.name) ?? false,
        );
        const categoryKeysAlreadySelected = new Set(selectedCategories.map(getCategoryKey));
        const templateCategoryKeys = template.categories.map(getCategoryKey);
        setTemplateContributions(
          withTemplateSelected({
            contributions: templateContributions,
            templateId: template.id,
            suppliedItemKeys: templateCategoryKeys,
            addedItemKeys: templateCategoryKeys.filter(
              (categoryKey) => !categoryKeysAlreadySelected.has(categoryKey),
            ),
          }),
        );
        // Chained, not re-derived from the `proposalBuilder` prop: the vendor team seated a few
        // lines above lives on `updatedProposalBuilder`, and starting again from the prop here
        // threw that away, so "add them to the team" added nobody.
        updatedProposalBuilder = updatedProposalBuilder.setFeeSchedule(categories);
      }
      updatedProposalBuilder = session.context?.viewingAsVendor
        ? updatedProposalBuilder.setVendorFeeScheduleTemplateIds(updatedSelectedTemplateIds)
        : updatedProposalBuilder.setClientFeeScheduleTemplateIds(updatedSelectedTemplateIds);

      onProposalBuilderUpdated(updatedProposalBuilder);
    } catch (error) {
      console.error(error);
      showErrorSnackbar(error, { doing: "applying the fee schedule template", operation: "proposal.fee-schedule.apply-template" });
    }
  }

  function handlePublishedFeeScheduleApplied(schedule: FeeSchedule) {
    void applyPublishedFeeScheduleToDraft({
      proposalBuilder,
      schedule,
      reconcileStaffing,
      getLatestProposalBuilder: () => latestDraftRef.current.proposalBuilder,
      onProposalBuilderUpdated: (updated) => latestDraftRef.current.onProposalBuilderUpdated(updated),
    });
  }

  // Supplements the recorded contributions for templates that were already checked when the tab
  // mounted, which have no record of their own. `templateFeeSchedules` is one ownership-filtered
  // page, so this can only ever KEEP a category a still-selected template supplies, never take one
  // away: what it cannot see is covered by the contribution records instead.
  function getCategoryKeysOfLoadedTemplates(templateIds: Guid[]): string[] {
    return templateFeeSchedules
      .filter((loadedTemplate) => templateIds.some((id) => id.isEqualTo(loadedTemplate.id)))
      .flatMap((loadedTemplate) => loadedTemplate.categories.map(getCategoryKey));
  }

  // No event to stop here any more: the panel's checkbox stops its own press before it reaches the
  // row, so a category is toggled once whether the checkbox or the row around it was pressed.
  async function handleTemplateCategoryToggled(
    selectedCategory: FeeScheduleTemplateCategory,
    template: FeeScheduleTemplate,
  ) {
    // Identity is by name, not id: a category added through the fee-schedule add panel is a value
    // snapshot with a freshly minted id (see the categorySource comment below), even when it started
    // as one of this template's own categories. Comparing by id would leave that category's checkbox
    // permanently unchecked here even though it is already in the proposal's fee schedule.
    const categoryWasSelected = selectedCategories.some((category) =>
      category.name?.isEqualTo(selectedCategory.name),
    );
    // One category off a template names a person just as a whole template does, so the same
    // question is asked for it.
    const staffing = categoryWasSelected
      ? undefined
      : await reconcileStaffing({
          categories: [selectedCategory],
          teamMembers: vendorTeamMembers,
          teamIsMasked: vendorTeamIsMasked,
          excludedLegalEntityId: teamRoster.excludedLegalEntityId,
        });
    if (staffing && !staffing.applied) return;
    const updatedCategories = categoryWasSelected
      ? selectedCategories.filter((category) => !category.name?.isEqualTo(selectedCategory.name))
      : [...selectedCategories, ...(staffing?.applied ? staffing.categories : [])];
    const updatedTemplateIds = getFeeScheduleTemplateIdsAfterCategoryToggled({
      selectedCategories: updatedCategories,
      selectedTemplateIds: selectedTemplateIds ?? [],
      template,
      toggledCategory: selectedCategory,
    });
    let updatedProposalBuilder = session.context?.viewingAsVendor
      ? proposalBuilder.setVendorFeeScheduleTemplateIds(updatedTemplateIds)
      : proposalBuilder.setClientFeeScheduleTemplateIds(updatedTemplateIds);
    if (staffing?.applied && staffing.membersToAddToTeam.length > 0) {
      updatedProposalBuilder = updatedProposalBuilder.setVendorTeam(
        withFeeScheduleStaffingSeated(currentVendorTeam ?? new VendorTeam({ members: [] }), staffing.membersToAddToTeam),
      );
    }
    updatedProposalBuilder = updatedProposalBuilder.setFeeSchedule(updatedCategories);

    onProposalBuilderUpdated(updatedProposalBuilder);
  }

  function handleCategoryRemoved(categoryName: FeeScheduleCategoryName) {
    const removedCategory = selectedCategories.find((category) => category.name?.isEqualTo(categoryName));
    const updatedCategories = selectedCategories.filter((category) => !category.name?.isEqualTo(categoryName));
    let updatedProposalBuilder = proposalBuilder.setFeeSchedule(updatedCategories);
    const currentTemplateIds = selectedTemplateIds ?? [];
    const removedCategoryTemplate = removedCategory
      ? templateFeeSchedules.find((template) =>
          template.categories.some((category) => category.id.isEqualTo(removedCategory.id)),
        )
      : undefined;

    if (removedCategory) {
      const updatedTemplateIds = removeSelectedFeeScheduleTemplateCategory({
        removedCategory,
        selectedCategories: updatedCategories,
        selectedTemplateIds: currentTemplateIds,
        template: removedCategoryTemplate,
      });
      updatedProposalBuilder = session.context?.viewingAsVendor
        ? updatedProposalBuilder.setVendorFeeScheduleTemplateIds(updatedTemplateIds)
        : updatedProposalBuilder.setClientFeeScheduleTemplateIds(updatedTemplateIds);
    }

    onProposalBuilderUpdated(updatedProposalBuilder);
  }

  // A revert restores a category's prior value through the SAME `setFeeSchedule` builder path any
  // other category edit uses (see FeeScheduleTemplatesTable's onEditCategory-driven callers), so it
  // flows to `queueProposalUpdate` as a `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw
  // `FieldSet`.
  function handleCategoryReverted(category: FeeScheduleTemplateCategory) {
    const updatedCategories = selectedCategories.some((existing) => existing.id.isEqualTo(category.id))
      ? selectedCategories.map((existing) => (existing.id.isEqualTo(category.id) ? category : existing))
      : [...selectedCategories, category];
    onProposalBuilderUpdated(proposalBuilder.setFeeSchedule(updatedCategories));
  }

  /**
   * Undo on a category the draft edited or removed since its last save: the saved category comes
   * back, and the template ids are recomputed from the rows (#6995). Reads the draft as of the latest
   * render, so the callback it is handed to the table can stay the same one across renders.
   *
   * The saved rate may name somebody the draft has since taken off the vendor team; removing them is
   * what cleared the rate and made it an edit. Putting that name back as it was would save a rate that
   * approval and hiring refuse. So the saved rate goes through the same staffing question a template's
   * rates do: put them back on the team, give the rate to somebody on it, or leave it unassigned.
   * Cancelling it undoes nothing.
   */
  const viewingAsVendor = session.context?.viewingAsVendor;
  const handleCategoryUndone = useCallback(
    async (category: FeeScheduleTemplateCategory) => {
      const savedCategory = savedCollections?.savedFeeScheduleCategory(category.id);
      if (!savedCollections || !savedCategory) return;
      const asked = latestDraftRef.current;
      if (savedCollections.feeScheduleCategoryNameConflict(category.id, asked.proposalBuilder.currentSpec)) return;
      const askedWorkAgreement = asked.proposalBuilder.currentSpec.workAgreement;
      const askedVendorTeam = Masked.isPresentNotMasked(askedWorkAgreement.vendorTeam) ? askedWorkAgreement.vendorTeam : undefined;
      const staffing = await asked.reconcileStaffing({
        categories: [savedCategory],
        teamMembers: getFeeScheduleTeamMembers(askedVendorTeam),
        teamIsMasked: Masked.isMasked(askedWorkAgreement.vendorTeam),
        excludedLegalEntityId: FeeScheduleTeamRoster.fromVendorTeam(
          askedWorkAgreement.vendorTeam,
          askedWorkAgreement.clientTeam,
        ).excludedLegalEntityId,
      });
      if (!staffing.applied) return;

      // The draft as it stands once the question is answered: a collaborator's save can land while the
      // dialog is open, as it can while published rates wait on it.
      const { proposalBuilder: latestBuilder, onProposalBuilderUpdated: publish } = latestDraftRef.current;
      const latestVendorTeam = latestBuilder.currentSpec.workAgreement.vendorTeam;
      const staffedBuilder = staffing.membersToAddToTeam.length > 0
        ? latestBuilder.setVendorTeam(
          withFeeScheduleStaffingSeated(
            Masked.isPresentNotMasked(latestVendorTeam) ? latestVendorTeam : new VendorTeam({ members: [] }),
            staffing.membersToAddToTeam,
          ),
        )
        : latestBuilder;
      const restored = savedCollections.restoreFeeScheduleCategory(category.id, staffedBuilder, staffing.categories[0]);
      if (restored === staffedBuilder) return;
      const restoredWorkAgreement = restored.currentSpec.workAgreement;
      if (!Masked.isPresentNotMasked(restoredWorkAgreement.feeSchedule)) return;
      const restoredCategories = restoredWorkAgreement.feeSchedule as FeeScheduleTemplateCategory[];
      const restoredRow = restoredCategories.find((row) => row.id.isEqualTo(category.id)) ?? category;
      const restoredTemplateIds = viewingAsVendor
        ? restoredWorkAgreement.vendorFeeScheduleTemplateIds
        : restoredWorkAgreement.clientFeeScheduleTemplateIds;
      // An edit may have renamed it, so the templates named by either name are recomputed.
      const templateIds = [category, restoredRow].reduce(
        (ids, restoredCategory) =>
          getFeeScheduleTemplateIdsAfterCategoryRestored({
            restoredCategory,
            selectedCategories: restoredCategories,
            selectedTemplateIds: ids,
            templates: templateFeeSchedules,
          }),
        restoredTemplateIds ?? [],
      );
      publish(
        viewingAsVendor
          ? restored.setVendorFeeScheduleTemplateIds(templateIds)
          : restored.setClientFeeScheduleTemplateIds(templateIds),
      );
    },
    [savedCollections, templateFeeSchedules, viewingAsVendor],
  );

  // Memoized for stability, not speed: the table draws these rows, and reports the rows it drew back
  // to the page, which re-renders this tab. A fresh array here on every render would never settle.
  const draftRemovedCategories = useMemo(
    () => savedCollections?.removedFeeScheduleCategories(proposalBuilder.currentSpec),
    [proposalBuilder.currentSpec, savedCollections],
  );
  const draftEditedCategoryIds = useMemo(() => {
    const feeSchedule = proposalBuilder.currentSpec.workAgreement.feeSchedule;
    if (!savedCollections || !Masked.isPresentNotMasked(feeSchedule)) return undefined;
    return new Set(
      (feeSchedule as FeeScheduleTemplateCategory[])
        .filter((category) => savedCollections.feeScheduleCategoryChange(category) === "edited")
        .map((category) => category.id.value),
    );
  }, [proposalBuilder.currentSpec, savedCollections]);
  // An edited category whose saved name another category has taken since: its Undo would leave two
  // categories of one name, so it waits until one of them is renamed or removed.
  const draftUndoBlockedReasons = useMemo(() => {
    if (!savedCollections || !draftEditedCategoryIds) return undefined;
    const reasons = new Map<string, string>();
    for (const categoryId of draftEditedCategoryIds) {
      const conflict = savedCollections.feeScheduleCategoryNameConflict(new Guid(categoryId), proposalBuilder.currentSpec);
      if (conflict) reasons.set(categoryId, undoBlockedByNameMessage(conflict.name.value));
    }
    return reasons;
  }, [draftEditedCategoryIds, proposalBuilder.currentSpec, savedCollections]);
  const draftChanges = useMemo<FeeScheduleDraftChanges | undefined>(
    () =>
      draftRemovedCategories && draftEditedCategoryIds
        ? {
            removedCategories: draftRemovedCategories,
            editedCategoryIds: draftEditedCategoryIds,
            undoBlockedReasons: draftUndoBlockedReasons,
            onCategoryUndone: (category) => void handleCategoryUndone(category),
          }
        : undefined,
    [draftEditedCategoryIds, draftRemovedCategories, draftUndoBlockedReasons, handleCategoryUndone],
  );

  function handleTemplateCreated(createdTemplate: CreatedFeeScheduleTemplate) {
    const nextTemplateIds = [...(selectedTemplateIds ?? []), createdTemplate.id];
    const updatedProposalBuilder = session.context?.viewingAsVendor
      ? proposalBuilder.setVendorFeeScheduleTemplateIds(nextTemplateIds)
      : proposalBuilder.setClientFeeScheduleTemplateIds(nextTemplateIds);

    // The new template arrives already checked, and it was built out of categories that were all
    // there beforehand — it added none of them but it supplies every one, which is what keeps them
    // from leaving with some other template the user later deselects.
    setTemplateContributions(
      withTemplateSelected({
        contributions: templateContributions,
        templateId: createdTemplate.id,
        suppliedItemKeys: selectedCategories.map(getCategoryKey),
        addedItemKeys: [],
      }),
    );
    onProposalBuilderUpdated(updatedProposalBuilder);
  }

  const createTemplate = useCreateFeeScheduleTemplateAction({
    categories: selectedCategories,
    proposalName: proposalBuilder.currentSpec.workAgreement?.name?.value,
    onTemplateCreated: handleTemplateCreated,
  });

  if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.feeSchedule)) {
    return maskedProposalFieldNotice(MaskedProposalField.FeeSchedule);
  }

  // The rows handed to the selector are the page the server already narrowed to `templateSearch`
  // through `templateQueryParams`'s `Query` — nothing here filters them again.
  const templateRows: TemplateSelectorRow[] = [];
  for (const template of templateFeeSchedules) {
    // One bad template among good ones, which is not the failure the panel's empty area speaks for:
    // the row stays where that template would have been and says so, and the rest still draw. It
    // is not a template the server's `Query` matched or missed — it is a row that failed to
    // hydrate at all — so it is shown regardless of what was typed.
    if (isFeeScheduleTemplateLoadIssue(template)) {
      templateRows.push({
        disabled: true,
        disabledReason: "This template could not be loaded",
        id: template.id?.value ?? TEMPLATE_LOAD_FAILURE_NAME,
        name: TEMPLATE_LOAD_FAILURE_NAME,
        selected: false,
        onSelectedChanged: ignoreSelection,
      });
      continue;
    }

    const templateRowId = template.id?.value ?? template.name;
    const templateIsSelected = selectedTemplateIds?.some((id) => id.isEqualTo(template.id)) ?? false;
    // By name, not id — see the identity comment in handleTemplateCategoryToggled.
    const categoryIsInUse = (category: FeeScheduleTemplateCategory) =>
      selectedCategories.some((selectedCategory) => selectedCategory.name?.isEqualTo(category.name));
    templateRows.push({
      // The categories the row's expander reveals in place, which is where the list's Collapse put
      // them. By name, not id — see the identity comment in handleTemplateCategoryToggled.
      children: template.categories.map((category: FeeScheduleTemplateCategory) => ({
        content: (
          <CategoryRowText>
            <TemplateRowName>
              <SearchMatch text={category.name.value} />
            </TemplateRowName>
            <CategoryRowDetail>
              {category.description?.value}
              <br />
              {category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.feeString}
            </CategoryRowDetail>
          </CategoryRowText>
        ),
        id: `${templateRowId}:${getCategoryKey(category)}`,
        name: category.name.value,
        selected: categoryIsInUse(category),
        onSelectedChanged: () => handleTemplateCategoryToggled(category, template),
      })),
      detail: <TemplateTagLine tags={template.tags.map((tag) => tag.value)} />,
      id: templateRowId,
      // Its categories are in the fee schedule without the template itself having been applied — the
      // rule the list's own checkbox carried, unchanged.
      indeterminate: !templateIsSelected && template.isUsingCategories(selectedCategories),
      inUse: { used: template.categories.filter(categoryIsInUse).length, total: template.categories.length },
      name: template.name,
      selected: templateIsSelected,
      onSelectedChanged: () => handleTemplateSelectionToggled(template),
    });
  }

  return (
    <>
      <ListContainer container>
        {!disableEditing && (
          <>
            <ResizableTemplatePanel minimized={isDrawerOpen} selectedCount={selectedTemplateCount}>
              {/*
                One panel, drawn once. The selector brings its own compact accordion, so the second
                copy this tab used to render behind a media query — and the stale duplicate of every
                control in it — is gone; `compact` is the pane measurement, not the viewport's.

                No `tabs`: there is one list of fee schedule templates in the domain and no second
                source to offer, so the strip stays off rather than being given a single tab to draw.
              */}
              <TemplateSelector
                action={
                  publishedRatesTeamId ? (
                    <PublishedFeeScheduleAction
                      marketplaceTeamId={publishedRatesTeamId}
                      onApply={handlePublishedFeeScheduleApplied}
                    />
                  ) : undefined
                }
                collapsedLabel="Fee Schedule Templates"
                compact={isMdDownDisplay}
                ownership={{
                  label: "fee schedule templates",
                  value: templateOwnershipScope,
                  onChange: setTemplateOwnershipScope,
                }}
                sorting={templateOrder.sorting}
                pagination={{
                  onChange: setTemplatePagination,
                  rowCount: paginatedTemplatesJSON?.headers.totalElements ?? 0,
                  value: templatePagination,
                }}
                rows={templateRows}
                search={{ onChange: handleTemplateSearchChanged, value: templateSearch }}
                selectedCount={selectedTemplateCount}
                status={{
                  isError: templatesQuery.isError,
                  isFetching: templatesQuery.isFetching,
                  isLoading: templatesQuery.isLoading,
                  onRetry: () => void templatesQuery.refetch(),
                }}
                subject="fee schedule templates"
              />
            </ResizableTemplatePanel>
            {!isMdDownDisplay && (
              <Grid>
                <Divider orientation="vertical" />
              </Grid>
            )}
          </>
        )}
        <FeeScheduleContent size="grow" sx={{ mt: 1 }}>
          <TabIssueList
            issues={issues ?? []}
            activeReviewField={activeReviewField}
            onIssueSelected={onIssueSelected ?? ((issue) => onTraverseToNewField?.(issue.field))}
          />
          <CategorySelection
            onDisplayedCategoriesChange={onDisplayedCategoriesChange}
            saveAsTemplateAction={disableEditing ? undefined : createTemplate.action}
            selectedCategories={selectedCategories}
            hideFromTemplateButton={true}
            teamRoster={teamRoster}
            teamMemberIssues={teamMemberIssues}
            feeIssues={feeIssues}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            disableEditing={disableEditing}
            fieldLockTooltip={fieldLockTooltip}
            activeReviewField={activeReviewField}
            pendingChangeFields={pendingChangeFields}
            onCreateCategoryClicked={onCreateCategoryClicked}
            onExistingCategoryClicked={onAddExistingCategoryClicked}
            onEditCategory={onEditCategory}
            onCategoryRemoved={handleCategoryRemoved}
            onCategoryReverted={handleCategoryReverted}
            draftChanges={draftChanges}
          />
        </FeeScheduleContent>
      </ListContainer>
      {createTemplate.dialog}
    </>
  );
}

/**
 * Why an edited category's Undo waits: another category already holds the name it would put back
 * (#6995).
 */
export function undoBlockedByNameMessage(name: string): string {
  return `Another category is named "${name}". Rename or remove it to undo this change.`;
}

/** What the panel says in place of a template that could not be read. */
const TEMPLATE_LOAD_FAILURE_NAME = "Error loading template.";

/** The page size the tab has always asked the fee-schedule template endpoint for. */
const INITIAL_TEMPLATE_PAGINATION = { pageIndex: 0, pageSize: PaginationParameters.DEFAULT_PAGE_SIZE };

/** The row that failed to load cannot be applied, so pressing it is not an act with a result. */
function ignoreSelection() {
  return undefined;
}

// By name, not id — see the identity comment in handleTemplateCategoryToggled. Name is therefore
// also the unit a template can be said to have contributed.
export function getCategoryKey(category: FeeScheduleTemplateCategory): string {
  return category.name?.value ?? "";
}

export function removeSelectedFeeScheduleTemplateId(selectedTemplateIds: Guid[], removedCategoryId: Guid) {
  return selectedTemplateIds.filter((id) => !id.isEqualTo(removedCategoryId));
}

// `selectedTemplateIds` (persisted as clientFeeScheduleTemplateIds/vendorFeeScheduleTemplateIds) is a
// list of whole-TEMPLATE ids, used only to render each template's own checkbox as checked. A fee
// schedule CATEGORY's id must never appear in it: the two id spaces share no meaning, and doing so
// both corrupts the "N Templates" count derived from this array's length and (since a category id
// never matches template.id) leaves the row's own checkbox unable to reflect the true state. Whether
// a template belongs in this list is entirely a function of whether every one of its categories is
// currently selected — recomputed here from `selectedCategories`, never from the toggled/removed
// category's id.
function withTemplateFullSelectionState(
  selectedTemplateIds: Guid[],
  selectedCategories: FeeScheduleTemplateCategory[],
  template?: FeeScheduleTemplate,
): Guid[] {
  if (!template?.id) return uniqFeeScheduleTemplateIds(selectedTemplateIds);

  // By name, not id — see the identity comment in handleTemplateCategoryToggled.
  const templateIsFullySelected =
    template.categories.length > 0 &&
    template.categories.every((category) =>
      selectedCategories.some((selectedCategory) => selectedCategory.name?.isEqualTo(category.name)),
    );
  const idsWithoutTemplate = selectedTemplateIds.filter((id) => !id.isEqualTo(template.id));

  return uniqFeeScheduleTemplateIds(
    templateIsFullySelected ? [...idsWithoutTemplate, template.id] : idsWithoutTemplate,
  );
}

export function getFeeScheduleTemplateIdsAfterCategoryToggled(args: {
  selectedCategories: FeeScheduleTemplateCategory[];
  selectedTemplateIds: Guid[];
  template: FeeScheduleTemplate;
  toggledCategory: FeeScheduleTemplateCategory;
}) {
  const { selectedCategories, selectedTemplateIds, template, toggledCategory } = args;
  // Defensive cleanup: strips the toggled category's id in case it (or another category id) leaked
  // into this list before this fix, in addition to recomputing the template's own id below.
  const idsWithoutToggledCategory = removeSelectedFeeScheduleTemplateId(selectedTemplateIds, toggledCategory.id);

  return withTemplateFullSelectionState(idsWithoutToggledCategory, selectedCategories, template);
}

/**
 * The template ids once an Undo has put `restoredCategory` back (#6995).
 *
 * Recomputed from the rows, never replayed: a template stays selected only while every one of its
 * categories is on the schedule, so its id comes back only when the Undo returns the last of them.
 * Every loaded template that holds the category, by id or by name, is recomputed — see the identity
 * comment in handleTemplateCategoryToggled.
 */
export function getFeeScheduleTemplateIdsAfterCategoryRestored(args: {
  restoredCategory: FeeScheduleTemplateCategory;
  selectedCategories: FeeScheduleTemplateCategory[];
  selectedTemplateIds: Guid[];
  templates: readonly FeeScheduleTemplate[];
}) {
  const { restoredCategory, selectedCategories, selectedTemplateIds, templates } = args;
  return templates
    .filter((template) =>
      template.categories.some(
        (category) => category.id.isEqualTo(restoredCategory.id) || category.name?.isEqualTo(restoredCategory.name),
      ),
    )
    .reduce(
      (templateIds, template) => withTemplateFullSelectionState(templateIds, selectedCategories, template),
      uniqFeeScheduleTemplateIds(selectedTemplateIds),
    );
}

export function removeSelectedFeeScheduleTemplateCategory(args: {
  removedCategory: FeeScheduleTemplateCategory;
  selectedCategories: FeeScheduleTemplateCategory[];
  selectedTemplateIds: Guid[];
  template?: FeeScheduleTemplate;
}) {
  const { removedCategory, selectedCategories, selectedTemplateIds, template } = args;
  // Defensive cleanup: strips a category id that leaked into this list before this fix (e.g. on a
  // proposal saved by an older client build), in addition to recomputing the template's own id.
  const idsWithoutRemovedCategory = removeSelectedFeeScheduleTemplateId(selectedTemplateIds, removedCategory.id);

  return withTemplateFullSelectionState(idsWithoutRemovedCategory, selectedCategories, template);
}

function uniqFeeScheduleTemplateIds(templateIds: Guid[]) {
  return _.uniqBy(templateIds, (id) => id.value);
}
